import React, { useRef } from 'react'
import { Trans, useTranslation } from 'lib/i18n'
import { pushToDataLayer } from 'lib/tracking'
import { useMediaQuery } from 'react-responsive'

import BusinessReviewCard, {
  BusinessReviewCardProps,
} from 'components/BusinessReviewCard/BusinessReviewCard'
import InfiniteHorizontalScrollLoop from 'components/InfiniteHorizontalScrollLoop/InfiniteHorizontalScrollLoop'
import Link from 'components/Link/Link'
import { Button } from 'components/UI/Button/Button'
import {
  BodySmall,
  DesktopH2,
  H3,
} from 'components/UI/Typography/Typography.new'
import Wrapper from 'components/Wrapper/Wrapper'

import styles from './BusinessReviewCardCollection.module.css'

type BusinessReviewCardCollectionProps = {
  businessReviewCardList: BusinessReviewCardProps[]
}

const BusinessReviewCardCollection: React.FC<
  BusinessReviewCardCollectionProps
> = ({ businessReviewCardList }) => {
  const { t } = useTranslation()
  const cardContainerRef = useRef<HTMLDivElement>()
  const isMaximum1024Width = useMediaQuery({ maxWidth: 1024 })

  const logCta = () =>
    pushToDataLayer({
      event: 'businessReviewCardsContactClick',
    })

  const ReviewCards = () => (
    <div className={styles.reviews} ref={cardContainerRef}>
      {businessReviewCardList.map((card) => (
        <div className={styles.card} key={card.id} data-cy="businessReviewCard">
          <BusinessReviewCard {...card} />
        </div>
      ))}
    </div>
  )

  return (
    <Wrapper edgeToEdge>
      <div className={styles.container} data-cy="businessReviewCardCollection">
        <div className={styles.title}>
          <DesktopH2 element="h2">
            <Trans i18nKey="BusinessReviewCardCollection.title">
              <span className={styles.blue}>Impact </span>you can feel
            </Trans>
          </DesktopH2>
        </div>
        {isMaximum1024Width ? (
          <ReviewCards />
        ) : (
          <InfiniteHorizontalScrollLoop
            surroundingBackup={1}
            steps={businessReviewCardList.length}
            scrollLength={774}
            intervalSeconds={7}
          >
            <ReviewCards />
          </InfiniteHorizontalScrollLoop>
        )}

        <div className={styles.CTABox}>
          <H3>
            {t(
              'BusinessReviewCardCollection.CTATitle',
              'Need something special?',
            )}
          </H3>
          <div className={styles.CTADescription}>
            <BodySmall>
              {t(
                'BusinessReviewCardCollection.CTADescription',
                'Contact one of our sales reps and we’ll help you create the best experience possible.',
              )}
            </BodySmall>
          </div>
          <div className={styles.buttonWrapper}>
            <Link href="/page/business-form" scroll>
              <a>
                <Button onClick={logCta} type="blue">
                  {t('BusinessReviewCardCollection.CTAButton', 'Contact us')}
                </Button>
              </a>
            </Link>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

export default BusinessReviewCardCollection
